import React, { useEffect, useMemo, useState } from 'react';

const STAGES = [
  { id: 'ploughing', label: 'Ploughing', description: 'Preparing the soil', accent: '#8b5e34', glow: 'rgba(139, 94, 52, 0.18)' },
  { id: 'planting', label: 'Planting', description: 'Seeds taking root', accent: '#16a34a', glow: 'rgba(22, 163, 74, 0.18)' },
  { id: 'maintenance', label: 'Maintenance', description: 'Water and care', accent: '#0284c7', glow: 'rgba(2, 132, 199, 0.18)' },
  { id: 'harvest', label: 'Harvest', description: 'Crops gathered', accent: '#ea580c', glow: 'rgba(234, 88, 12, 0.18)' },
  { id: 'processing', label: 'Processing', description: 'Sorted for dispatch', accent: '#7c3aed', glow: 'rgba(124, 58, 237, 0.18)' },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const resolveStageIndex = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return clamp(Math.round(value), 0, STAGES.length - 1);
  }

  const matchedIndex = STAGES.findIndex((stage) => stage.id === value);
  return matchedIndex >= 0 ? matchedIndex : null;
};

const getStageIndexFromProgress = (progress = 0) => {
  const safeProgress = clamp(Number(progress) || 0, 0, 100);
  if (safeProgress <= 20) return 0;
  if (safeProgress <= 40) return 1;
  if (safeProgress <= 60) return 2;
  if (safeProgress <= 80) return 3;
  return 4;
};

function StageVisual({ stageId, state, compact }) {
  const size = compact ? 52 : 64;
  const isActive = state === 'active';
  const isCompleted = state === 'completed';
  const activeClass = isActive ? ` loader-${stageId}-active` : '';
  const inactiveOpacity = isActive || isCompleted ? 1 : 0.35;

  if (isCompleted) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #16a34a, #22c55e)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: compact ? '0.9rem' : '1rem',
          fontWeight: 900,
          boxShadow: '0 12px 24px rgba(34, 197, 94, 0.22)',
        }}
      >
        ✔️
      </div>
    );
  }

  if (stageId === 'ploughing') {
    return (
      <div className={`loader-stage-visual${activeClass}`} style={{ width: size + 8, height: size, opacity: inactiveOpacity }}>
        <div className="loader-plough-scene">
          <div className="loader-plough-soil" />
          <div className="loader-plough-body" />
          <div className="loader-plough-cabin" />
          <div className="loader-plough-wheel loader-plough-wheel-small" />
          <div className="loader-plough-wheel loader-plough-wheel-large" />
          <div className="loader-plough-blade" />
        </div>
      </div>
    );
  }

  if (stageId === 'planting') {
    return (
      <div className={`loader-stage-visual${activeClass}`} style={{ width: size, height: size, opacity: inactiveOpacity }}>
        <div className="loader-plant-scene">
          <div className="loader-plant-soil" />
          <div className="loader-seed loader-seed-left" />
          <div className="loader-seed loader-seed-right" />
          <div className="loader-sprout-stem" />
          <div className="loader-sprout-leaf loader-sprout-leaf-left" />
          <div className="loader-sprout-leaf loader-sprout-leaf-right" />
        </div>
      </div>
    );
  }

  if (stageId === 'maintenance') {
    return (
      <div className={`loader-stage-visual${activeClass}`} style={{ width: size, height: size, opacity: inactiveOpacity }}>
        <div className="loader-maintenance-scene">
          <div className="loader-water-can" />
          <div className="loader-can-handle" />
          <div className="loader-can-spout" />
          <div className="loader-water-drop loader-water-drop-1" />
          <div className="loader-water-drop loader-water-drop-2" />
          <div className="loader-water-drop loader-water-drop-3" />
          <div className="loader-water-ground" />
        </div>
      </div>
    );
  }

  if (stageId === 'harvest') {
    return (
      <div className={`loader-stage-visual${activeClass}`} style={{ width: size, height: size, opacity: inactiveOpacity }}>
        <div className="loader-harvest-scene">
          <div className="loader-harvest-ground" />
          <div className="loader-crop loader-crop-1" />
          <div className="loader-crop loader-crop-2" />
          <div className="loader-crop loader-crop-3" />
          <div className="loader-scythe-handle" />
          <div className="loader-scythe-blade" />
        </div>
      </div>
    );
  }

  return (
    <div className={`loader-stage-visual${activeClass}`} style={{ width: size, height: size, opacity: inactiveOpacity }}>
      <div className="loader-process-scene">
        <div className="loader-mill-post" />
        <div className="loader-mill-hub" />
        <div className="loader-mill-blade loader-mill-blade-1" />
        <div className="loader-mill-blade loader-mill-blade-2" />
        <div className="loader-mill-blade loader-mill-blade-3" />
        <div className="loader-mill-blade loader-mill-blade-4" />
        <div className="loader-process-base" />
      </div>
    </div>
  );
}

export default function FarmLifecycleLoader({
  title = 'Preparing your fresh farm experience',
  subtitle = 'From field work to doorstep delivery, we are moving through each stage.',
  compact = false,
  progress = null,
  activeStage = null,
  completedStage = null,
  autoAdvance = true,
  intervalMs = 1200,
}) {
  const [autoIndex, setAutoIndex] = useState(0);

  useEffect(() => {
    if (!autoAdvance || progress !== null || activeStage !== null) return undefined;

    const intervalId = window.setInterval(() => {
      setAutoIndex((current) => (current + 1) % STAGES.length);
    }, Math.max(700, intervalMs));

    return () => window.clearInterval(intervalId);
  }, [activeStage, autoAdvance, intervalMs, progress]);

  const activeIndex = useMemo(() => {
    const explicitIndex = resolveStageIndex(activeStage);
    if (explicitIndex !== null) return explicitIndex;
    if (progress !== null) return getStageIndexFromProgress(progress);
    return autoIndex;
  }, [activeStage, autoIndex, progress]);

  const doneIndex = useMemo(() => {
    if (Number(progress) >= 100) return STAGES.length - 1;
    const explicitDoneIndex = resolveStageIndex(completedStage);
    if (explicitDoneIndex !== null) return explicitDoneIndex;
    return clamp(activeIndex - 1, -1, STAGES.length - 1);
  }, [activeIndex, completedStage, progress]);

  const progressRatio = useMemo(() => {
    if (progress !== null) return clamp((Number(progress) || 0) / 100, 0, 1);
    return clamp((activeIndex + 1) / STAGES.length, 0, 1);
  }, [activeIndex, progress]);

  return (
    <div
      style={{
        minHeight: compact ? '240px' : '360px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: compact ? '20px 14px' : '38px 20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: compact ? '860px' : '1120px',
          background: 'linear-gradient(180deg, #fcfdfb 0%, #f3f7f4 100%)',
          border: '1px solid #dbe7dd',
          borderRadius: compact ? '24px' : '30px',
          padding: compact ? '20px 18px' : '28px 26px',
          boxShadow: '0 18px 44px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: compact ? '1fr' : 'repeat(5, minmax(0, 1fr))',
            gap: compact ? '12px' : '14px',
            overflow: 'hidden',
            padding: 20
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: compact ? '28px' : '9%',
              right: compact ? 'auto' : '9%',
              top: compact ? '26px' : '34px',
              bottom: compact ? '26px' : 'auto',
              width: compact ? '4px' : 'auto',
              height: compact ? 'auto' : '4px',
              background: '#d7dfda',
              borderRadius: '999px',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: compact ? '28px' : '9%',
              right: compact ? 'auto' : '9%',
              top: compact ? '26px' : '34px',
              bottom: compact ? '26px' : 'auto',
              width: compact ? '4px' : 'auto',
              height: compact ? 'auto' : '4px',
              background: 'linear-gradient(90deg, #8b5e34, #16a34a, #0284c7, #ea580c, #7c3aed)',
              borderRadius: '999px',
              transformOrigin: compact ? 'top center' : 'left center',
              transform: compact ? `scaleY(${progressRatio})` : `scaleX(${progressRatio})`,
              transition: 'transform 360ms ease',
            }}
          />

          {STAGES.map((stage, index) => {
            const state = index <= doneIndex ? 'completed' : index === activeIndex ? 'active' : 'pending';
            const isActive = state === 'active';
            const isCompleted = state === 'completed';

            return (
              <div
                key={stage.id}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'grid',
                  gridTemplateColumns: compact ? '64px 1fr' : '1fr',
                  gap: compact ? '12px' : '10px',
                  alignItems: 'center',
                  textAlign: compact ? 'left' : 'center',
                  padding: compact ? '14px 12px' : '18px 14px',
                  borderRadius: '20px',
                  background: isActive ? 'linear-gradient(180deg, #ffffff 0%, #f8fbf8 100%)' : isCompleted ? '#f0fdf4' : 'rgba(255,255,255,0.8)',
                  border: `1px solid ${isActive ? stage.accent : isCompleted ? '#86efac' : '#e5e7eb'}`,
                  boxShadow: isActive ? `0 16px 30px ${stage.glow}` : 'none',
                  transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
                  opacity: isActive || isCompleted ? 1 : 0.84,
                  transition: 'all 300ms ease',
                }}
              >
                <div style={{ display: 'flex', justifyContent: compact ? 'flex-start' : 'center' }}>
                  <StageVisual stageId={stage.id} state={state} compact={compact} />
                </div>

                <div>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '28px',
                      height: '28px',
                      padding: 10,
                      borderRadius: '999px',
                      background: isActive ? stage.accent : isCompleted ? '#166534' : '#e5e7eb',
                      color: isActive || isCompleted ? 'white' : '#6b7280',
                      fontSize: '12px',
                      fontWeight: 800,
                      marginBottom: '8px',
                    }}
                  >
                  {stage.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <style>
          {`
            .loader-stage-visual {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: opacity 280ms ease, transform 280ms ease, filter 280ms ease;
              filter: grayscale(0.45);
            }

            .loader-stage-visual.loader-ploughing-active,
            .loader-stage-visual.loader-planting-active,
            .loader-stage-visual.loader-maintenance-active,
            .loader-stage-visual.loader-harvest-active,
            .loader-stage-visual.loader-processing-active {
              filter: grayscale(0);
            }

            .loader-plough-scene,
            .loader-plant-scene,
            .loader-maintenance-scene,
            .loader-harvest-scene,
            .loader-process-scene {
              position: relative;
              width: 100%;
              height: 100%;
            }

            .loader-plough-soil {
              position: absolute;
              left: 6px;
              right: 6px;
              bottom: 10px;
              height: 16px;
              border-radius: 999px;
              background: linear-gradient(180deg, #caa27d, #8b5e34);
            }

            .loader-plough-body {
              position: absolute;
              left: 14px;
              bottom: 24px;
              width: 30px;
              height: 16px;
              border-radius: 10px 14px 8px 8px;
              background: #166534;
            }

            .loader-plough-cabin {
              position: absolute;
              left: 22px;
              bottom: 36px;
              width: 14px;
              height: 12px;
              border-radius: 6px 6px 3px 3px;
              background: #bbf7d0;
            }

            .loader-plough-wheel {
              position: absolute;
              border-radius: 50%;
              background: #0f172a;
            }

            .loader-plough-wheel-small {
              left: 16px;
              bottom: 18px;
              width: 12px;
              height: 12px;
            }

            .loader-plough-wheel-large {
              left: 36px;
              bottom: 15px;
              width: 18px;
              height: 18px;
            }

            .loader-plough-blade {
              position: absolute;
              left: 50px;
              bottom: 23px;
              width: 12px;
              height: 4px;
              border-radius: 999px;
              background: #6b4423;
              transform-origin: left center;
            }

            .loader-ploughing-active .loader-plough-body,
            .loader-ploughing-active .loader-plough-cabin {
              animation: loader-plough-bob 1.6s ease-in-out infinite;
            }

            .loader-ploughing-active .loader-plough-wheel {
              animation: loader-wheel-spin 0.85s linear infinite;
            }

            .loader-ploughing-active .loader-plough-blade {
              animation: loader-plough-slice 1.2s ease-in-out infinite;
            }

            .loader-plant-soil,
            .loader-water-ground,
            .loader-harvest-ground,
            .loader-process-base {
              position: absolute;
              left: 10px;
              right: 10px;
              bottom: 10px;
              height: 10px;
              border-radius: 999px;
              background: #cbd5e1;
            }

            .loader-plant-soil {
              background: linear-gradient(180deg, #bf8d5e, #8b5e34);
            }

            .loader-seed {
              position: absolute;
              top: 10px;
              width: 8px;
              height: 10px;
              border-radius: 50%;
              background: #854d0e;
              opacity: 0;
            }

            .loader-seed-left {
              left: 25px;
            }

            .loader-seed-right {
              left: 35px;
            }

            .loader-sprout-stem {
              position: absolute;
              left: 31px;
              bottom: 20px;
              width: 4px;
              height: 0;
              border-radius: 999px;
              background: #15803d;
            }

            .loader-sprout-leaf {
              position: absolute;
              bottom: 34px;
              width: 13px;
              height: 8px;
              background: #22c55e;
              opacity: 0;
            }

            .loader-sprout-leaf-left {
              left: 21px;
              border-radius: 100% 10% 100% 10%;
              transform: rotate(-28deg) scale(0.2);
              transform-origin: right bottom;
            }

            .loader-sprout-leaf-right {
              left: 32px;
              border-radius: 10% 100% 10% 100%;
              transform: rotate(28deg) scale(0.2);
              transform-origin: left bottom;
            }

            .loader-planting-active .loader-seed-left {
              animation: loader-seed-drop 1.9s ease-in-out infinite;
            }

            .loader-planting-active .loader-seed-right {
              animation: loader-seed-drop 1.9s ease-in-out 0.2s infinite;
            }

            .loader-planting-active .loader-sprout-stem {
              animation: loader-sprout-grow 1.9s ease-in-out infinite;
            }

            .loader-planting-active .loader-sprout-leaf-left {
              animation: loader-leaf-grow-left 1.9s ease-in-out infinite;
            }

            .loader-planting-active .loader-sprout-leaf-right {
              animation: loader-leaf-grow-right 1.9s ease-in-out infinite;
            }

            .loader-water-can {
              position: absolute;
              left: 14px;
              top: 18px;
              width: 28px;
              height: 18px;
              border-radius: 9px 9px 6px 6px;
              background: #0ea5e9;
              transform-origin: 65% 85%;
            }

            .loader-can-handle {
              position: absolute;
              left: 11px;
              top: 16px;
              width: 12px;
              height: 12px;
              border: 3px solid #38bdf8;
              border-right: none;
              border-radius: 999px 0 0 999px;
            }

            .loader-can-spout {
              position: absolute;
              left: 38px;
              top: 23px;
              width: 16px;
              height: 4px;
              border-radius: 999px;
              background: #0284c7;
              transform: rotate(20deg);
              transform-origin: left center;
            }

            .loader-water-drop {
              position: absolute;
              top: 27px;
              width: 6px;
              height: 10px;
              border-radius: 999px 999px 999px 999px / 85% 85% 30% 30%;
              background: #38bdf8;
              opacity: 0;
            }

            .loader-water-drop-1 {
              left: 45px;
            }

            .loader-water-drop-2 {
              left: 51px;
            }

            .loader-water-drop-3 {
              left: 57px;
            }

            .loader-maintenance-active .loader-water-can,
            .loader-maintenance-active .loader-can-spout {
              animation: loader-water-tilt 1.6s ease-in-out infinite;
            }

            .loader-maintenance-active .loader-water-drop-1 {
              animation: loader-water-fall 1.4s linear infinite;
            }

            .loader-maintenance-active .loader-water-drop-2 {
              animation: loader-water-fall 1.4s linear 0.16s infinite;
            }

            .loader-maintenance-active .loader-water-drop-3 {
              animation: loader-water-fall 1.4s linear 0.32s infinite;
            }

            .loader-crop {
              position: absolute;
              bottom: 18px;
              width: 4px;
              height: 24px;
              border-radius: 999px;
              background: #65a30d;
            }

            .loader-crop::before,
            .loader-crop::after {
              content: '';
              position: absolute;
              width: 10px;
              height: 4px;
              border-radius: 999px;
              background: #facc15;
            }

            .loader-crop::before {
              left: -8px;
              top: 4px;
              transform: rotate(-34deg);
            }

            .loader-crop::after {
              left: 2px;
              top: 11px;
              transform: rotate(34deg);
            }

            .loader-crop-1 {
              left: 20px;
            }

            .loader-crop-2 {
              left: 30px;
            }

            .loader-crop-3 {
              left: 40px;
            }

            .loader-scythe-handle {
              position: absolute;
              left: 42px;
              top: 8px;
              width: 4px;
              height: 34px;
              border-radius: 999px;
              background: #7c2d12;
              transform-origin: bottom center;
            }

            .loader-scythe-blade {
              position: absolute;
              left: 34px;
              top: 10px;
              width: 20px;
              height: 10px;
              border-radius: 50% 100% 100% 50%;
              border: 3px solid #cbd5e1;
              border-left: none;
              border-bottom: none;
              transform-origin: 70% 85%;
            }

            .loader-harvest-active .loader-scythe-handle,
            .loader-harvest-active .loader-scythe-blade {
              animation: loader-scythe-swing 1.5s ease-in-out infinite;
            }

            .loader-harvest-active .loader-crop {
              animation: loader-crop-sway 1.3s ease-in-out infinite;
            }

            .loader-mill-post {
              position: absolute;
              left: 31px;
              bottom: 18px;
              width: 4px;
              height: 22px;
              border-radius: 999px;
              background: #64748b;
            }

            .loader-mill-hub {
              position: absolute;
              left: 24px;
              top: 18px;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: #7c3aed;
            }

            .loader-mill-blade {
              position: absolute;
              left: 31px;
              top: 27px;
              width: 22px;
              height: 6px;
              border-radius: 999px;
              background: #c4b5fd;
              transform-origin: left center;
            }

            .loader-mill-blade-1 {
              transform: rotate(0deg);
            }

            .loader-mill-blade-2 {
              transform: rotate(90deg);
            }

            .loader-mill-blade-3 {
              transform: rotate(180deg);
            }

            .loader-mill-blade-4 {
              transform: rotate(270deg);
            }

            .loader-process-base {
              background: linear-gradient(180deg, #cbd5e1, #94a3b8);
            }

            .loader-processing-active .loader-mill-hub,
            .loader-processing-active .loader-mill-blade {
              animation: loader-mill-spin 1.25s linear infinite;
            }

            @keyframes loader-wheel-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }

            @keyframes loader-plough-bob {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-2px); }
            }

            @keyframes loader-plough-slice {
              0%, 100% { transform: rotate(0deg); }
              50% { transform: rotate(10deg); }
            }

            @keyframes loader-seed-drop {
              0% { transform: translateY(0); opacity: 0; }
              20% { opacity: 1; }
              55% { transform: translateY(20px); opacity: 1; }
              100% { transform: translateY(24px); opacity: 0; }
            }

            @keyframes loader-sprout-grow {
              0%, 28% { height: 0; }
              55% { height: 20px; }
              100% { height: 20px; }
            }

            @keyframes loader-leaf-grow-left {
              0%, 50% { opacity: 0; transform: rotate(-28deg) scale(0.2); }
              72%, 100% { opacity: 1; transform: rotate(-28deg) scale(1); }
            }

            @keyframes loader-leaf-grow-right {
              0%, 50% { opacity: 0; transform: rotate(28deg) scale(0.2); }
              72%, 100% { opacity: 1; transform: rotate(28deg) scale(1); }
            }

            @keyframes loader-water-tilt {
              0%, 100% { transform: rotate(0deg); }
              35%, 70% { transform: rotate(-16deg); }
            }

            @keyframes loader-water-fall {
              0% { transform: translateY(0) scaleY(0.7); opacity: 0; }
              18% { opacity: 1; }
              85% { transform: translateY(20px) scaleY(1.1); opacity: 1; }
              100% { transform: translateY(24px) scaleY(0.8); opacity: 0; }
            }

            @keyframes loader-scythe-swing {
              0%, 100% { transform: rotate(28deg); }
              50% { transform: rotate(-22deg); }
            }

            @keyframes loader-crop-sway {
              0%, 100% { transform: rotate(0deg); }
              50% { transform: rotate(8deg); }
            }

            @keyframes loader-mill-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
}
